import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ContactDataDto, ContactDataForm } from '@bo-client/shared/models/contactDataDto';
import { ConnectionObject, ConnectionObjectForm } from '@bo-client/shared/models/connectionObject';
import { TypedForm, CombinedForm } from '@bo-client/shared/forms/generic-forms/typedForm.type';
import { UploadForm } from '@bo-client/shared/models/documentDto';
import { ConnectionObjectAdditionalInfoModel } from '@bo-client/activation-management/shared/models/connection-object-additional-info.model';
import { DocumentDtoModel } from '@bo-client/shared/models/documentDto';
import { MeteringConceptModel } from '@bo-client/activation-management/shared/models/meteringConceptModel';
import {
  FeedingPlantTypeEnum,
  FeedInManagementInfoEnum,
  GridRegistrationTypeEnum,
  MeterOperatorEnum,
  PowerGenerationSourceEnum
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import {
  ProducerForm,
  ProducerModel
} from '@bo-client/activation-management/grid-registration/model/producer.model';
import {
  StorageForm,
  StorageModel
} from '@bo-client/activation-management/grid-registration/model/storage.model';
import {
  PVModuleForm,
  PVModuleModel
} from '@bo-client/activation-management/grid-registration/model/pv-module.model';
import { ActivityDto } from '@bo-client/shared/models/activityDto';
import { RequestModel } from '@bo-client/shared/models/request.model';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { GridRegistrationReferenceModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { BankAccountModel } from '@bo-client/shared/models/bankAccountModel';
import { SepaMandateModel } from '@bo-client/shared/models/sepaMandateModel';
import { PaymentTypeEnum } from '@bo-client/shared/constants/enums';
import { CompensationCategoryModel } from '@bo-client/activation-management/shared/models/compensationCategoryModel';

export class FeederContractModel extends RequestModel {
  contractType: string;
  commissioningDate?: Date;
  meterInstallationDate?: Date;
  deviceNo?: string;
  compensationCategories?: CompensationCategoryModel[];
  gridRegistrationType: GridRegistrationTypeEnum;
  customer: ContactDataDto = {};
  invoiceContact: ContactDataDto = {};
  connectionObject: ConnectionObject = {};
  connectionObjectAdditionalInfo: ConnectionObjectAdditionalInfoModel =
    new ConnectionObjectAdditionalInfoModel();
  uniqueCoId: string;
  companyLocation: CompanyLocationDto;
  uploads: DocumentDtoModel[] = [];
  meteringConcept: MeteringConceptModel = new MeteringConceptModel();
  powerGenerationSource: PowerGenerationSourceEnum;
  meterOperator: MeterOperatorEnum;
  isEEGExtention: boolean;
  feedingPlantType: FeedingPlantTypeEnum;
  producers: ProducerModel[] = [];
  storages: StorageModel[] = [];
  pvModules: PVModuleModel[] = [];
  activities: ActivityDto[];
  storageConcept: MeteringConceptModel = new MeteringConceptModel();
  feedInManagementInfo: FeedInManagementInfoEnum;
  consumptionOver30kw: boolean;

  hasDifferentInvoiceContact = false;
  hasStorage = false;

  comment: string;

  shortDescription?: string;
  longDescription?: string;

  // End Customer fields
  bankAccount?: BankAccountModel;
  paymentType?: PaymentTypeEnum;
  sepaMandate?: SepaMandateModel;
  customerToken?: string;
  plantOperatorAndEndUserAreIdentical?: boolean;
  maStRNumberProducer?: string;
  maStRNumberStorage?: string;
  maStRNumber?: string;
  maStRAcceptedDeclaration: boolean;
  maStRRegistrationDate: Date;
  ustNr: string;
  taxNr: string;
  ustPflichtig: boolean;
  steuerArt: string;
  gridRegistration?: GridRegistrationReferenceModel;
  acceptedUstConditions: boolean;
  externalId?: string;
  externalGridRegistrationId?: string;
  confirmationWithoutContract?: boolean;

  // New fields
  maStRSELNumber?: string; // MaStR Nr. der Stromerzeugungslokation der Einheit
  maStREEGNumber?: string; // MaStR Nr. der Erzeugungsanlage (EEG Nummer)
  maStRSSENumberStorage?: string; // MaStR SSE des Speichers
  maStREEGNumberStorage?: string; // MaStR EEG der Erzeugungsanlage (SEE Nummer)
  batteryTechnology?: string; // Batterietechnologie
  batteryType?: string; // Batterietyp (LIT, ...)
  usageType?: string; // MaStR Nutzungsart
  orientation?: string; // Ausrichtung
  tilt?: string; // Neigung
  secondaryOrientation?: string; // Nebenausrichtung
  secondaryTilt?: string; // Neigung Nebenausrichtung
  eegProducerKey?: string; // EEG-Anlagenschlüssel der Erzeugungseinheit
  eegStorageKey?: string; // EEG-Anlagenschlüssel der Speichereinheit

  customerTaxIdentificationNumber?: string; // Steueridentifikationsnummer
  fullFeedInBonusRequested: boolean;
}

type FeederContractScalarForm = TypedForm<
  Pick<
    FeederContractModel,
    | 'id'
    | 'state'
    | 'contractType'
    | 'stateModifiedDate'
    | 'commissioningDate'
    | 'meterInstallationDate'
    | 'deviceNo'
    | 'compensationCategories'
    | 'plantOperatorAndEndUserAreIdentical'
    | 'maStRNumberProducer'
    | 'maStRNumberStorage'
    | 'maStRNumber'
    | 'maStRAcceptedDeclaration'
    | 'maStRRegistrationDate'
    | 'maStRSELNumber'
    | 'maStREEGNumber'
    | 'maStRSSENumberStorage'
    | 'maStREEGNumberStorage'
    | 'batteryTechnology'
    | 'batteryType'
    | 'usageType'
    | 'orientation'
    | 'tilt'
    | 'secondaryOrientation'
    | 'secondaryTilt'
    | 'ustNr'
    | 'taxNr'
    | 'customerTaxIdentificationNumber'
    | 'meterOperator'
    | 'ustPflichtig'
    | 'steuerArt'
    | 'paymentType'
    | 'acceptedUstConditions'
    | 'shortDescription'
    | 'longDescription'
    | 'externalId'
    | 'externalGridRegistrationId'
    | 'feedInManagementInfo'
    | 'powerGenerationSource'
    | 'isEEGExtention'
    | 'feedingPlantType'
    | 'consumptionOver30kw'
    | 'comment'
    | 'hasStorage'
  >
>;

type FeederContractBankAccountForm = TypedForm<
  Pick<NonNullable<FeederContractModel['bankAccount']>, 'iban' | 'bic' | 'holder' | 'bankName'>
>;

type FeederContractSepaMandateForm = TypedForm<
  Pick<
    NonNullable<FeederContractModel['sepaMandate']>,
    'acceptedSepa' | 'sepaAcceptedDate' | 'validFrom' | 'validTo'
  >
>;

type FeederContractAdditionalInfoForm = TypedForm<
  Pick<
    ConnectionObjectAdditionalInfoModel,
    | 'isNewBuilding'
    | 'buildingType'
    | 'numberOfFlats'
    | 'numberOfUsers'
    | 'numberOfOtherUsers'
    | 'objectUsage'
  >
>;

type FeederContractComplexForm = FormGroup<{
  bankAccount: FeederContractBankAccountForm;
  sepaMandate: FeederContractSepaMandateForm;
  connectionObject: ConnectionObjectForm;
  additionalInfoConnectionObject: FeederContractAdditionalInfoForm;
  filiationData: FormArray<ContactDataForm>;
  hasDifferentInvoiceContact: FormControl<boolean>;
  invoiceContact: FormArray<ContactDataForm>;
  pvModules: FormArray<PVModuleForm>;
  producers: FormArray<ProducerForm>;
  storages: FormArray<StorageForm>;
  uploads: FormArray<UploadForm>;
}>;

/** Editor form for feeder contract confirmation — see docs/technical/request-entity/feeder-contract/forms/feeder-contract-form.md */
export type FeederContractForm = CombinedForm<FeederContractScalarForm, FeederContractComplexForm>;

export class ConfirmFeederContractModel {
  contractType: string;
  commissioningDate?: Date;
  meterInstallationDate?: Date;
  deviceNo?: string;
  compensationCategories?: CompensationCategoryModel[];
  invoiceContact?: ContactDataDto;
  plantOperatorAndEndUserAreIdentical?: boolean;
  maStRNumberProducer?: string;
  maStRNumberStorage?: string;
  maStRNumber?: string;
  maStRAcceptedDeclaration: boolean;
  maStRRegistrationDate: Date;
  ustNr: string;
  taxNr: string;
  ustPflichtig: boolean;
  steuerArt: string;
  bankAccount?: BankAccountModel;
  paymentType?: PaymentTypeEnum;
  sepaMandate?: SepaMandateModel;
  producers: ProducerModel[] = [];
  storages: StorageModel[] = [];
  acceptedUstConditions: boolean;
}

export class FeederContractReferenceModel {
  id: number;
}

export enum FeederContractStatusEnum {
  ToBeCreated = 'ToBeCreated',
  Created = 'Created',
  TechnicallyReviewed = 'TechnicallyReviewed',
  ConfirmationRequestedByMail = 'ConfirmationRequestedByMail',
  Accepted = 'Accepted',
  Confirmed = 'Confirmed',
  Cancelled = 'Cancelled',
  FeedbackNeeded = 'FeedbackNeeded',
  Completed = 'Completed',
  ClosedWithoutFeedback = 'ClosedWithoutFeedback'
}
